/**
 * Loading — 全屏加载遮罩组件
 *
 * 用法：
 *   window.loading.show();            // 显示（可选文字）
 *   window.loading.show('处理中...');  // 带提示文字
 *   window.loading.hide();            // 隐藏
 *
 * 支持嵌套调用（引用计数），多处 show() 后需等量 hide() 才真正隐藏：
 *   window.loading.show();  // count = 1
 *   window.loading.show();  // count = 2
 *   window.loading.hide();  // count = 1，仍可见
 *   window.loading.hide();  // count = 0，隐藏
 *
 * 强制隐藏（不管引用计数）：
 *   window.loading.hide(true);
 */

(function () {
  'use strict';

  // ── CSS 延迟加载 ───────────────────────────────────────────────────────────

  let _cssLoaded = false;

  function loadCSS() {
    if (_cssLoaded) return;
    _cssLoaded = true;

    const scriptSrc =
      (document.currentScript && document.currentScript.src) ||
      (() => {
        const scripts = Array.from(document.querySelectorAll('script[src]'));
        const match = scripts.find((s) => s.src.includes('loading.js'));
        return match ? match.src : '';
      })();

    if (scriptSrc) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = scriptSrc.replace('loading.js', 'loading.css');
      document.head.appendChild(link);
    }
  }

  // ── DOM 创建 ───────────────────────────────────────────────────────────────

  let _overlay = null;
  let _textEl = null;

  function createOverlay() {
    if (_overlay) return;

    _overlay = document.createElement('div');
    _overlay.id = 'loading-overlay';
    _overlay.setAttribute('role', 'progressbar');
    _overlay.setAttribute('aria-label', 'Loading');
    _overlay.setAttribute('aria-hidden', 'true');

    _textEl = document.createElement('span');
    _textEl.className = 'loading-spinner__text';

    _overlay.innerHTML = `
      <div class="loading-spinner">
        <div class="loading-spinner__ring"></div>
      </div>
    `;

    // 将文字节点插入 spinner 中
    _overlay.querySelector('.loading-spinner').appendChild(_textEl);

    document.body.appendChild(_overlay);
  }

  // ── LoadingManager ─────────────────────────────────────────────────────────

  let _count = 0;
  let _hideTimer = null;

  const LoadingManager = {
    /**
     * 显示 loading 遮罩
     * @param {string} [text]  可选提示文字
     */
    show(text) {
      loadCSS();

      // 确保 DOM 就绪后再创建
      if (document.body) {
        createOverlay();
      } else {
        document.addEventListener('DOMContentLoaded', () => createOverlay(), { once: true });
        return this;
      }

      // 清除延迟隐藏
      if (_hideTimer) {
        clearTimeout(_hideTimer);
        _hideTimer = null;
      }

      _count = Math.max(_count, 0) + 1;

      // 更新提示文字
      if (_textEl) {
        _textEl.textContent = text || '';
        _textEl.style.display = text ? '' : 'none';
      }

      // 每次 show 时重新 append 到 body 末尾，确保始终在所有弹窗之上
      if (_overlay.parentElement !== document.body || document.body.lastElementChild !== _overlay) {
        document.body.appendChild(_overlay);
      }

      _overlay.setAttribute('aria-hidden', 'false');
      _overlay.classList.add('is-visible');

      return this;
    },

    /**
     * 隐藏 loading 遮罩
     * @param {boolean} [force=false]  true 则忽略引用计数强制隐藏
     */
    hide(force = false) {
      if (!_overlay) return this;

      if (force) {
        _count = 0;
      } else {
        _count = Math.max(_count - 1, 0);
      }

      if (_count > 0) return this;

      // 稍微延迟，避免同步 show/hide 闪烁
      _hideTimer = setTimeout(() => {
        if (_count > 0) return;
        _overlay.classList.remove('is-visible');
        _overlay.setAttribute('aria-hidden', 'true');
        _hideTimer = null;
      }, 30);

      return this;
    },

    /** 当前是否可见 */
    get visible() {
      return _count > 0;
    },
  };

  // ── 全局挂载 ───────────────────────────────────────────────────────────────

  window.loading = LoadingManager;
})();
